import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';
import { formatCurrencyINR } from '../../../utils/currency';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useDemo } from '../../../contexts/DemoContext';
import { getChartTruncationConfig } from '../../../utils/chartUtils';
import { useUserData } from '../../../hooks/useUserData';

const InvestmentTimelineChart = ({ investments }) => {
  const { userData } = useUserData();
  const { canProjectBeyond5Years } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const chartData = useMemo(() => {
    if (!investments || investments.length === 0) return [];

    const currentYear = new Date().getFullYear();
    const maxYear = Math.max(
      ...investments.map(inv => inv.endYear || currentYear + 20),
      currentYear + 20
    );

    const years = [];
    for (let year = currentYear; year <= maxYear; year++) {
      const yearData = { year: String(year) };
      
      // Calculate total investment for this year
      let totalInvestment = 0;
      investments.forEach(inv => {
        if (!inv.isActive) return;
        if (year < inv.startYear || (inv.endYear && year > inv.endYear)) return;
        
        const freq = inv.frequency || 'monthly';
        const amount = inv.amount || 0;
        let annualAmount = 0;
        
        if (freq === 'monthly') annualAmount = amount * 12;
        else if (freq === 'quarterly') annualAmount = amount * 4;
        else if (freq === 'annually') annualAmount = amount;
        else if (freq === 'one-time') annualAmount = year === inv.startYear ? amount : 0;
        
        totalInvestment += annualAmount;
      });
      
      yearData.totalInvestment = totalInvestment;
      years.push(yearData);
    }

    return years;
  }, [investments]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
          <p className="font-medium text-foreground mb-2">Year {label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrencyINR(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (!investments || investments.length === 0) {
    return (
      <div className="h-64 bg-muted/30 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <div className="text-muted-foreground mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-muted-foreground">No investment data available</p>
          <p className="text-sm text-muted-foreground">Add investments to see your timeline</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" style={{ height: '256px' }}>
      {(() => {
        const currentYear = new Date().getFullYear();
        const fullTotalYears = chartData.length;
        const truncationConfig = getChartTruncationConfig('InvestmentTimeline', canProjectBeyond5Years, isDemoModeActive);
        const shouldTruncate = truncationConfig.shouldTruncate;
        const freeYearsLimit = 5;
        const displayYearsLimit = freeYearsLimit * 2; // 10 years for free users only
        const rawData = shouldTruncate ? chartData.slice(0, displayYearsLimit + 1) : chartData;
        const displayData = rawData.map((row, idx) => ({
          ...row,
          isBlurred: shouldTruncate && idx > freeYearsLimit
        }));
        
        // Show year labels every 2 years
        const xAxisTicks = [];
        displayData.forEach((d, i) => {
          if (i % 2 === 0) {
            xAxisTicks.push(d.year);
          }
        });

        return (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={displayData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="blurGradientInvestment" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity="1" />
                    <stop offset="48%" stopColor="#3B82F6" stopOpacity="1" />
                    <stop offset="55%" stopColor="#3B82F6" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.15" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="year" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  ticks={xAxisTicks}
                  height={50}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  tickFormatter={(value) => formatCurrencyINR(value, { compact: true })}
                />
                <Tooltip 
                  content={(props) => {
                    if (props.active && props.payload && props.payload.length) {
                      const isBlurred = props.payload[0]?.payload?.isBlurred;
                      if (isBlurred) {
                        return (
                          <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
                            <p className="font-medium text-muted-foreground flex items-center gap-2">
                              <Icon name="Lock" size={14} />
                              Locked Content
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Upgrade to view full projections
                            </p>
                          </div>
                        );
                      }
                      return <CustomTooltip {...props} />;
                    }
                    return null;
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="totalInvestment" 
                  stroke={shouldTruncate ? 'url(#blurGradientInvestment)' : '#3B82F6'}
                  strokeWidth={3}
                  dot={false}
                  name="Total Annual Investment"
                  strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "0"}
                />
              </LineChart>
            </ResponsiveContainer>
            
            {/* Blurred overlay with gradient and CTA */}
            {shouldTruncate && fullTotalYears > freeYearsLimit && (
              <div 
                className="absolute top-0 right-0 pointer-events-none"
                style={{ 
                  left: '48%',
                  bottom: '50px',
                  background: 'linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.5) 8%, rgba(255, 255, 255, 0.85) 25%, rgba(255, 255, 255, 0.95) 60%)',
                  backdropFilter: 'blur(5px)',
                }}
              >
                {/* Dark mode gradient overlay */}
                <div 
                  className="absolute inset-0 dark:block hidden"
                  style={{ 
                    background: 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.4) 8%, rgba(0, 0, 0, 0.75) 25%, rgba(0, 0, 0, 0.9) 60%)',
                  }}
                />
                
                {/* Upgrade CTA Card */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                  <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-2xl p-4 w-[180px] text-white">
                    <div className="flex items-center justify-center mb-3">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                        <Icon name="Lock" size={22} className="text-white" />
                      </div>
                    </div>
                    <h4 className="font-bold text-center mb-1 text-sm">
                      {(userData?.setup?.projectionHorizon || 20) - freeYearsLimit}+ Years Locked
                    </h4>
                    <p className="text-xs text-center text-white/90 mb-3 leading-tight">
                      Long-term allocation strategy continues.
                    </p>
                    <a
                      href="/upgrade"
                      className="block w-full bg-white text-amber-600 font-semibold text-sm py-2 px-3 rounded-lg text-center hover:bg-amber-50 transition-colors shadow-md"
                    >
                      See My Full Picture
                    </a>
                  </div>
                </div>
                
                {/* Vertical separator line at truncation point */}
                <div 
                  className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-amber-400 to-transparent opacity-70"
                  style={{ 
                    boxShadow: '0 0 12px rgba(251, 191, 36, 0.7)'
                  }}
                />
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
};

export default InvestmentTimelineChart;



